import React, { useEffect, useMemo, useState } from "react";
import { collection, onSnapshot, query, where } from "firebase/firestore";
import { Link } from "react-router-dom";
import { db } from "../../firebase/config";
import { trackUsageEvent } from "../Analytics/CampaignTrafficTracker";
import { AuthActionGate, useAuthActionGate } from "../Common/AuthActionGate";
import { formatProductPrice } from "../../utils/productDisplay";
import {
  getStandKindLabel,
  getPublicShelfProducts,
  isPublicFarmStand,
} from "../../utils/farmStandPublic";
import "./PublicFarmStands.css";

const getStandLocation = (stand = {}) =>
  stand.publicLocationLabel ||
  stand.locationText ||
  [stand.city, stand.state].filter(Boolean).join(", ") ||
  "Location not listed";

const getStandName = (stand = {}) =>
  stand.standName || stand.name || stand.businessName || "Farm stand";

const getSellerName = (stand = {}) =>
  stand.sellerName ||
  stand.farmName ||
  stand.businessName ||
  stand.ownerName ||
  "Local farm";

const PublicFarmStands = ({ currentUser }) => {
  const [stands, setStands] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const { requireSignIn, gateProps } = useAuthActionGate(currentUser);

  useEffect(() => {
    const standsQuery = query(collection(db, "farmStands"), where("status", "in", ["active", "open"]));
    const unsubscribe = onSnapshot(
      standsQuery,
      (snapshot) => {
        setStands(snapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() })));
        setLoading(false);
      },
      () => {
        setStands([]);
        setLoading(false);
      }
    );
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const productsQuery = query(collection(db, "products"), where("status", "==", "active"));
    const unsubscribe = onSnapshot(
      productsQuery,
      (snapshot) => {
        setProducts(snapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() })));
      },
      () => setProducts([])
    );
    return () => unsubscribe();
  }, []);

  const visibleStands = useMemo(
    () =>
      stands
        .filter(
          (stand) =>
            isPublicFarmStand(stand)
        )
        .sort((a, b) => getStandName(a).localeCompare(getStandName(b))),
    [stands]
  );

  return (
    <main className="page-shell page-shell-wide public-farm-stands-page">
      <div className="page-topbar">
        <div>
          <p className="eyebrow">Public discovery</p>
          <h1>Farm Stands &amp; Pickup Spots</h1>
          <p className="page-subtitle">
            Browse published farm stands, pickup spots, and public shelf items.
          </p>
        </div>
        <button
          type="button"
          className="btn-primary"
          onClick={() => requireSignIn("Create a free account or sign in to add a farm stand.")}
        >
          Add a Farm Stand
        </button>
      </div>

      <AuthActionGate {...gateProps} />

      {loading ? (
        <section className="public-farm-stands-empty"><p>Loading public farm stands...</p></section>
      ) : visibleStands.length === 0 ? (
        <section className="public-farm-stands-empty">
          <h2>No public farm stands or pickup spots yet</h2>
          <p>Published stands and pickup spots will appear here as local farms open their shelves.</p>
        </section>
      ) : (
        <div className="public-farm-stands-grid">
          {visibleStands.map((stand) => {
            const shelfProducts = getPublicShelfProducts(products, stand);
            return (
              <article className="public-farm-stand-card" key={stand.id}>
                {stand.standImage ? (
                  <img src={stand.standImage} alt={getStandName(stand)} className="public-farm-stand-image" loading="lazy" />
                ) : (
                  <div className="public-farm-stand-image public-farm-stand-image-empty">Farm stand</div>
                )}
                <div className="public-farm-stand-body">
                  <p className="eyebrow">{getSellerName(stand)}</p>
                  <h2>{getStandName(stand)}</h2>
                  <span className="public-farm-stand-kind">{getStandKindLabel(stand)}</span>
                  <p className="public-farm-stand-location">{getStandLocation(stand)}</p>
                  {stand.description && <p>{stand.description}</p>}
                  {stand.hoursText && <p><strong>Hours:</strong> {stand.hoursText}</p>}
                  {shelfProducts.length > 0 && (
                    <div className="public-farm-stand-shelf">
                      <h3>Live Shelf</h3>
                      {shelfProducts.map((product) => (
                        <div className="public-farm-stand-item" key={product.id}>
                          <span>{product.name || "Farm item"}</span>
                          <span>
                            {formatProductPrice(product)}
                            {product.quantityAvailable != null
                              ? " - " + product.quantityAvailable + " available"
                              : ""}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                  <Link
                    className="btn-soft public-farm-stand-action"
                    to={"/stand/" + stand.id}
                    onClick={() => trackUsageEvent("public_stand_open", {
                      feature: "farm_stands",
                      action: "open",
                      relatedId: stand.id,
                    })}
                  >
                    {getStandKindLabel(stand) === "Pickup Spot" ? "View Pickup Spot" : "View Live Shelf"}
                  </Link>
                </div>
              </article>
            );
          })}
        </div>
      )}
    </main>
  );
};

export default PublicFarmStands;
